import type { CSSProperties } from "react";
import { WeekHoursEditDay } from "./WeekHoursEditDay";
import type { WeekBlock } from "./weekHoursMath";
export { weekMinuteAt, weekMoveBlock, weekMoveEdge, type WeekBlock } from "./weekHoursMath";

/** The weekly calendar (the working hours round, 2026-11-01; brief docs/briefs/working-hours-round.md rule 3, the approved mock-up). Seven
 *  day columns, the hours 00 to 24 down the side at 20 px an hour. Two faces:
 *  - the editor (`mode: "edit"`): one week of hours (the workspace's default or one person's), every block of every day; the day's head (a
 *    button) or a click on the column picks the day (the screen draws the lines under the grid); with `onChange` each block has a grip at its
 *    top and bottom to drag or move with the arrow keys, and its body moves with a mouse (the hours editor round, 2026-11-02,
 *    `WeekHoursEditDay`); `tint` dresses the editor in a person's colour (`--av`); a day off has no block; without either it only reads;
 *  - the team view (`mode: "team"`): one thin lane per person inside every day, the viewer's first, each in the person's colour (`tint`, the one
 *    colour passed inline as `--av`), and a "now" line in today's column.
 *  Props in, events out: the screen works out the blocks (the shift into the viewer's zone lives in `@omnabase/shared`). On a phone the grid
 *  keeps its minimum width and scrolls inside its card (`.ob-weekhours`), never the page. Semantic tokens only (`styles/weekhours.css`). */
export type WeekDay = "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun";
export type WeekHoursWeek = { v: 2; days: Record<WeekDay, WeekBlock[]> };
/** a stretch of one day column in minutes from midnight */
export type WeekSpan = { from: number; to: number };
export type WeekLane = { id: string; name: string; tint: string; days: Record<WeekDay, WeekSpan[]> };
export type WeekHoursProps =
  | { mode: "edit"; hours: WeekHoursWeek; weekStart?: "monday" | "sunday"; selected?: WeekDay | null; onSelect?: (day: WeekDay) => void; onChange?: (hours: WeekHoursWeek) => void; tint?: string; label?: string }
  | { mode: "team"; lanes: WeekLane[]; viewerId: string; now?: { day: WeekDay; minutes: number } | null; weekStart?: "monday" | "sunday"; label?: string };

export const WEEK_HOUR_PX = 20;
/** minutes from midnight to pixels down the grid */
export const weekHoursPx = (minutes: number): number => (minutes * WEEK_HOUR_PX) / 60;
const MONDAY_FIRST: WeekDay[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_NAMES: Record<WeekDay, string> = { Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday", Sun: "Sunday" };
/** the order of the columns: Monday first, or Sunday first when the workspace's week starts on Sunday */
export const weekDayOrder = (weekStart: "monday" | "sunday"): WeekDay[] => (weekStart === "sunday" ? ["Sun", ...MONDAY_FIRST.slice(0, 6)] : [...MONDAY_FIRST]);
/** the viewer's lane first, the rest in their order */
export function weekLaneOrder<T extends { id: string }>(lanes: T[], viewerId: string): T[] {
  return [...lanes.filter((l) => l.id === viewerId), ...lanes.filter((l) => l.id !== viewerId)];
}
/** the weekday and minute of `at` in a zone, for the "now" line */
export function weekNowMark(at: string | Date, zone: string): { day: WeekDay; minutes: number } {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: zone, hourCycle: "h23", weekday: "short", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(at)).map((p) => [p.type, p.value]));
  return { day: parts.weekday as WeekDay, minutes: (Number(parts.hour) % 24) * 60 + Number(parts.minute) };
}
/** the eight person colours (design tokens, light and dark); one per person by a stable hash of their id, so a colleague keeps their colour */
export const PERSON_TINTS = 8;
export function personTint(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return `var(--ob-person-${(h % PERSON_TINTS) + 1})`;
}

const px = (n: number) => `${n}px`;
const place = (from: number, to: number): CSSProperties => ({ top: px(weekHoursPx(from)), height: px(weekHoursPx(to - from)) });
const HOURS = Array.from({ length: 25 }, (_, h) => h);

export function WeekHours(props: WeekHoursProps) {
  const days = weekDayOrder(props.weekStart ?? "monday");
  const today = props.mode === "team" ? props.now?.day ?? null : null;
  const tint = props.mode === "edit" ? props.tint : undefined;
  return (
    <div className={`ob-weekhours ob-weekhours--${props.mode}${tint ? " ob-weekhours--tinted" : ""}`} role="group" aria-label={props.label ?? "Working hours"} style={tint ? ({ "--av": tint } as CSSProperties) : undefined}>
      <div className="ob-weekhours__grid">
        <span className="ob-weekhours__corner" aria-hidden="true" />
        {days.map((d) => <DayHead key={d} day={d} today={d === today} selected={props.mode === "edit" && props.selected === d} onSelect={props.mode === "edit" ? props.onSelect : undefined} />)}
        <div className="ob-weekhours__axis" aria-hidden="true">{HOURS.map((h) => <span key={h} className="ob-weekhours__hour" style={{ top: px(h * WEEK_HOUR_PX) }}>{String(h).padStart(2, "0")}</span>)}</div>
        {props.mode === "edit" ? days.map((d) => <EditDay key={d} day={d} props={props} />)
          : days.map((d) => <TeamDay key={d} day={d} lanes={weekLaneOrder(props.lanes, props.viewerId)} now={props.now?.day === d ? props.now.minutes : null} />)}
      </div>
    </div>
  );
}

/** a day's head: a button that picks the day when the screen lets days be picked, else a plain label */
function DayHead({ day, today, selected, onSelect }: { day: WeekDay; today: boolean; selected: boolean; onSelect?: (day: WeekDay) => void }) {
  const cls = `ob-weekhours__dayhead${today ? " ob-weekhours__dayhead--today" : ""}${selected ? " ob-weekhours__dayhead--selected" : ""}`;
  if (!onSelect) return <span className={cls} aria-hidden="true">{day}</span>;
  return <button type="button" className={cls} aria-label={DAY_NAMES[day]} aria-pressed={selected} onClick={() => onSelect(day)}>{day}</button>;
}

/** one column of the editor, its blocks handed to the editing column with the week's change wired back */
function EditDay({ day, props }: { day: WeekDay; props: Extract<WeekHoursProps, { mode: "edit" }> }) {
  const { hours, onSelect, onChange } = props;
  return (
    <WeekHoursEditDay day={day} dayName={DAY_NAMES[day]} blocks={hours.days[day]} selected={props.selected === day} weekend={day === "Sat" || day === "Sun"}
      onSelect={onSelect ? () => onSelect(day) : undefined} onChange={onChange ? (blocks: WeekBlock[]) => onChange({ v: 2, days: { ...hours.days, [day]: blocks } }) : undefined} />
  );
}

/** one column of the team view: a lane per person, the now line where today is */
function TeamDay({ day, lanes, now }: { day: WeekDay; lanes: WeekLane[]; now: number | null }) {
  return (
    <div className={`ob-weekhours__day${day === "Sat" || day === "Sun" ? " ob-weekhours__day--weekend" : ""}${now !== null ? " ob-weekhours__day--today" : ""}`} role="group" aria-label={DAY_NAMES[day]}>
      <div className="ob-weekhours__lanes" style={{ "--lanes": Math.max(1, lanes.length) } as CSSProperties}>
        {lanes.map((l) => (
          <div key={l.id} className="ob-weekhours__lane" data-member={l.id} title={l.name} style={{ "--av": l.tint } as CSSProperties}>
            {l.days[day].map((s) => <span key={`${s.from}-${s.to}`} className="ob-weekhours__block" style={place(s.from, s.to)} />)}
          </div>
        ))}
      </div>
      {now !== null && <span className="ob-weekhours__now" style={{ top: px(weekHoursPx(now)) }} aria-hidden="true" />}
    </div>
  );
}
